import React, { useState } from 'react';
import { X, MapPin, Calendar, Clock, Save, Edit3, CheckCircle } from 'lucide-react';

const QuickGameEditor = ({ 
  selectedSchedule, 
  availableGrounds, 
  schedulesService,
  onScheduleUpdate,
  onClose 
}) => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [editedGame, setEditedGame] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const formatGameDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString();
};

  const handleGameClick = (weekIndex, gameIndex, game) => {
    const gameData = {
      weekIndex,
      gameIndex,
      week: selectedSchedule.schedule_data[weekIndex].week,
      ...game
    };
    setSelectedGame(gameData);
    setEditedGame({ ...gameData });
  };

const handleSave = async () => {
  if (!editedGame || !selectedGame) return;
  
  setSaving(true);
  try {
    console.log('💾 Starting save for game:', editedGame);
    
    // Create a deep copy of the schedule data
    const updatedScheduleData = JSON.parse(JSON.stringify(selectedSchedule.schedule_data));
    
    // Update the specific game
    const game = updatedScheduleData[selectedGame.weekIndex].games[selectedGame.gameIndex];
    
    console.log('📝 Original game:', game);
    
    // Update game properties
    game.homeTeam = editedGame.homeTeam;
    game.awayTeam = editedGame.awayTeam;
    game.date = editedGame.date || null;
    game.time = editedGame.time || null;
    
    // Handle ground assignment
 // Handle ground assignment
if (editedGame.ground_id) {
  const selectedGround = availableGrounds.find(g => g.id === editedGame.ground_id);
  if (selectedGround) {
    game.ground_id = selectedGround.id;
    game.ground_name = selectedGround.name || selectedGround.ground_name;
    game.ground_city = selectedGround.city;
    game.ground_state = selectedGround.state;
    game.ground_address_1 = selectedGround.address_1;
    game.ground_address_2 = selectedGround.address_2;
    game.ground_zip_code = selectedGround.zip_code;
    game.ground_country = selectedGround.country;
  }
} else {
  // Clear ground assignment
  game.ground_id = null;
  game.ground_name = null;
  game.ground_city = null;
  game.ground_state = null;
  game.ground_address_1 = null;
  game.ground_address_2 = null;
  game.ground_zip_code = null;
  game.ground_country = null;
}
    
    console.log('✏️ Updated game:', game);
    console.log('📦 Full updated schedule data:', updatedScheduleData);
    
    const updatePayload = {
      gameEdit: true,
      scheduleData: updatedScheduleData
    };

    // Save to database
    await schedulesService.update(selectedSchedule.id, updatePayload);
    
    console.log('✅ Database update complete');
    
    // Close the edit panel
    setSelectedGame(null);
    setEditedGame(null);
    
    // Show success message
    setShowSuccessMessage(true);
    
    console.log('🔄 Calling onScheduleUpdate...');
    
    // CRITICAL: Wait for database to fully commit before refreshing
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Notify parent component to refresh the main view
    await onScheduleUpdate();
    
    console.log('✅ Parent update complete');
    
    // Hide success message after parent updates
    setTimeout(() => setShowSuccessMessage(false), 2000);
    
  } catch (error) {
    console.error('❌ Error saving game:', error);
    alert('Failed to save changes. Please try again.');
  } finally {
    setSaving(false);
  }
};

  const handleCancel = () => {
    setSelectedGame(null);
    setEditedGame(null);
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '600px', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '8px',
      position: 'relative'
    }}>
      {/* Success Message */}
      {showSuccessMessage && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 2000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <CheckCircle size={20} />
          <span style={{ fontWeight: '500' }}>Changes saved successfully!</span>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Edit3 size={24} color="#3b82f6" />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Quick Game Editor</h2>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Instruction */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: '#eff6ff',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '14px',
        color: '#1e40af'
      }}>
        Click on any game below to edit its details
      </div>

      {/* Games List - Scrollable */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        position: 'relative'
      }}>
        <div style={{ display: 'grid', gap: '16px' }}>
          {selectedSchedule.schedule_data
            .sort((a, b) => parseInt(a.week) - parseInt(b.week))
            .map((week, weekIndex) => (
              <div key={weekIndex}>
                <h3 style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  marginBottom: '12px',
                  color: '#374151'
                }}>
                  Week/Round {week.week}
                </h3>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {week.games && week.games.map((game, gameIndex) => (
                    <div
                      key={gameIndex}
                      onClick={() => handleGameClick(weekIndex, gameIndex, game)}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: 
                          selectedGame?.weekIndex === weekIndex && selectedGame?.gameIndex === gameIndex 
                            ? '#dbeafe' 
                            : 'white'
                      }}
                      onMouseEnter={(e) => {
                        if (!(selectedGame?.weekIndex === weekIndex && selectedGame?.gameIndex === gameIndex)) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!(selectedGame?.weekIndex === weekIndex && selectedGame?.gameIndex === gameIndex)) {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <div style={{ fontWeight: '500', color: '#111827' }}>
                          {game.homeTeam} vs {game.awayTeam}
                        </div>
                        <Edit3 size={14} color="#6b7280" />
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        fontSize: '12px',
                        color: '#6b7280',
                        flexWrap: 'wrap'
                      }}>
                        {game.ground_name && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <MapPin size={12} />
                            {game.ground_name}
                          </span>
                        )}
                     {game.date && (
  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
    <Calendar size={12} />
    {formatGameDate(game.date)}
  </span>
)}
                        {game.time && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={12} />
                            {game.time}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Floating Edit Panel */}
      {selectedGame && editedGame && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '500px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid #e5e7eb',
          zIndex: 1000,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          {/* Edit Panel Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f9fafb'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
              Edit Game - Week {selectedGame.week}
            </h3>
            <button
              onClick={handleCancel}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Edit Form */}
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'grid', gap: '16px' }}>
              {/* Home Team */}
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  marginBottom: '6px',
                  color: '#374151'
                }}>
                  Home Team
                </label>
                <input
                  type="text"
                  value={editedGame.homeTeam || ''}
                  onChange={(e) => setEditedGame({ ...editedGame, homeTeam: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Away Team */}
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  marginBottom: '6px',
                  color: '#374151'
                }}>
                  Away Team
                </label>
                <input
                  type="text"
                  value={editedGame.awayTeam || ''}
                  onChange={(e) => setEditedGame({ ...editedGame, awayTeam: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Ground */}
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  marginBottom: '6px',
                  color: '#374151'
                }}>
                  Venue
                </label>
                <select
                  value={editedGame.ground_id || ''}
                  onChange={(e) => setEditedGame({ ...editedGame, ground_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">No ground assigned</option>
                  {availableGrounds.map(ground => (
                    <option key={ground.id} value={ground.id}>
                      {ground.name} - {ground.city}, {ground.state}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  marginBottom: '6px',
                  color: '#374151'
                }}>
                  Date
                </label>
                <input
                  type="date"
                  value={editedGame.date || ''}
                  onChange={(e) => setEditedGame({ ...editedGame, date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Time */}
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  marginBottom: '6px',
                  color: '#374151'
                }}>
                  Time
                </label>
                <input
                  type="time"
                  value={editedGame.time || ''}
                  onChange={(e) => setEditedGame({ ...editedGame, time: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '20px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  color: '#374151',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.target.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  backgroundColor: saving ? '#93c5fd' : '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  opacity: saving ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.target.style.backgroundColor = '#2563eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving) {
                    e.target.style.backgroundColor = '#3b82f6';
                  }
                }}
              >
                <Save size={16} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop overlay when editing */}
      {selectedGame && (
        <div
          onClick={handleCancel}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
        />
      )}
    </div>
  );
};

export default QuickGameEditor;